import {
  EntitlementPlatform,
  MNGSession,
  SubscriptionLevel,
} from '@Modules/index';

export type EntitlementDecoded = Record<string, any> & {
  aud: string;
  verifiedEmail: boolean;
  uuid: string;
  email: string;
  entitlement_source?: string;
  entitlement_method?: string;
  entitlement_entitled: boolean;
  entitlement_expiry: number | null;
  entitlement_extras_adfree?: boolean;
  entitlement_isEedition: boolean;
  entitlement_serviceCode: string;
  entitlement_paper?: string;
  entitlement_level?: SubscriptionLevel;
  entitlement_organizationName?: string;
  iat: number;
};

export type Entitlement = EntitlementDecoded & {
  authState: string; // "<level>-<serviceCode>" with fallbacks
  entitledLevel: string; // human-readable label
};

type KnownOrAnon = SubscriptionLevel | 'anonymous';

const ENTITLED_LABELS: Record<KnownOrAnon, string> = {
  limited: 'Limited Access - Subscribe now for access',
  subscriber: 'Standard Digital Access',
  freeTrial: 'Standard Digital Access',
  premium: 'Premium Ad-Free Access',
  comp: 'Standard Digital Trial',
  'partner-access': 'Partner Access',
  anonymous: 'Anonymous',
};

export function toEntitledLevel(e: EntitlementDecoded): string {
  const level =
    (e.entitlement_level as SubscriptionLevel | undefined) ?? 'anonymous';
  let label = ENTITLED_LABELS[level];

  if (e.entitlement_source === 'partner-access') {
    const org = e.entitlement_organizationName?.trim();
    if (org) label = `${label} provided by ${org}`;
  }
  return label;
}

// Concatenate level & service code with robust fallbacks
export function toAuthState(e: EntitlementDecoded): string {
  const level = (e.entitlement_level?.trim() || 'anonymous').toLowerCase();
  const service = e.entitlement_serviceCode?.toString().trim() || 'unknown';
  return `${level}-${service}`;
}

// Public builder: upgrade EntitlementDecoded -> EntitlementExtra
export function buildEntitlementExtra(e: EntitlementDecoded): Entitlement {
  return {
    ...e,
    authState: toAuthState(e),
    entitledLevel: toEntitledLevel(e),
  };
}

// const entitleLevelMap = {

// } as Record<string, SubscriptionLevel>;

//brand as Jwt so random string can't be passed in.
export type Jwt = string & { readonly __brand: 'JWT' };
export const asJwt = (s: string) => s as Jwt;

export const isExpired = (
  exp: number | null,
  now: number = Math.floor(Date.now() / 1000),
) => exp !== null && now >= exp;

export const hasMinimumRequiredAttributes = (
  entitlement: EntitlementDetails,
): boolean =>
  entitlement.decoded.uuid != undefined &&
  entitlement.decoded.entitlement_entitled != undefined &&
  entitlement.decoded.aud != undefined &&
  entitlement.decoded.iat != undefined;

// ---- Result helper + guards ----
export type Ok<T> = { ok: true; value: T };
export type Err<E> = { ok: false; error: E };
export type Result<T, E = unknown> = Ok<T> | Err<E>;

export const isOk = <T, E>(r: Result<T, E>): r is Ok<T> => r.ok === true;
export const isErr = <T, E>(r: Result<T, E>): r is Err<E> => r.ok === false;

export type LocalSource = 'localstorage' | 'cookie';
export type ServerSource = 'create' | 'refresh';

export type EntitlementSourceKind = 'local' | 'server' | 'webview_jwt';

export type LocalSourceDetails = { source: LocalSource; kind: 'local' };
export type ServerSourceDetails = { source: ServerSource; kind: 'server' };
export type WebViewSourceDetails = {
  source: 'webview_jwt';
  kind: 'webview_jwt';
};

export type EntitlementSource =
  | LocalSourceDetails
  | ServerSourceDetails
  | WebViewSourceDetails
  | { source: 'none'; kind: 'none' };

export type WebViewEntitlementResult = {
  queryParamDetected: boolean;
  isValid: boolean;
  validationError?: string;
  entitlement?: EntitlementDetails;
};

//   export type EntitlementSource =
//     | { path: LocalSource; mode: 'local' }
//     | { path: ServerSource; mode: 'server' };

export type LocalFoundValid = {
  state: 'valid';
  entitlement: EntitlementWithToken;
} & LocalSourceDetails;

export type LocalFoundExpired = {
  state: 'expired';
  entitlement: EntitlementWithToken;
} & LocalSourceDetails;

export type LocalFoundUserMismatch = {
  state: 'userMismatch';
  entitlement: EntitlementDetails;
} & LocalSourceDetails;

export type LocalMissing = {
  state: 'missing';
} & LocalSourceDetails;

export type LocalCheck =
  LocalFoundValid | LocalFoundExpired | LocalMissing | LocalFoundUserMismatch;

export type LocalCheckResult = {
  results: {
    localStorage: LocalCheck;
    cookie: LocalCheck;
  };
  anyValid: LocalFoundValid | null;
  firstExpired: LocalFoundExpired | null;
  firstMissing: LocalCheck | null;
  firstUserMismatch: LocalFoundUserMismatch | null;
};

export type TokenExpires = {
  exp: number | null;
  expiresAt: string;
};

export type TokenIssued = {
  iat: number;
  issuedAt: string;
};

export type Expirable<T> = T & {
  issued: TokenIssued;
  expires: TokenExpires;
};

export type EntitlementDetails = Expirable<{
  source?: EntitlementSourceKind;
  decoded: EntitlementDecoded;
  session: MNGSession;
}>;

export type EntitlementWithToken = {
  token: Jwt;
  jwt: EntitlementDetails;
};

export type CreateSessionRequestPayload = {
  uuid: string;
  publication: string;
  platform: EntitlementPlatform;
};
export type RefreshSessionRequestPayload = {
  jwt: Jwt;
  verified: boolean;
};

export type SessionResult = {
  jwt: string;
};

export type EntitlementRequest =
  | {
      action: 'create';
      endpoint: 'session/create';
      payload: CreateSessionRequestPayload;
    }
  | {
      action: 'refresh';
      endpoint: 'session/refresh';
      payload: RefreshSessionRequestPayload;
    };

export type LocalReaders = {
  readLocalStorage: () => Result<EntitlementWithToken | null>;
  readCookie: () => Result<EntitlementWithToken | null>;
};

export type ServerClient = {
  exchange: (req: EntitlementRequest) => Promise<Result<EntitlementWithToken>>;
};

export type EntitlementSnapshot =
  | ({
      status: 'valid';
      entitlement: EntitlementDetails;
    } & { source: EntitlementSource })
  | {
      status: 'none' | 'error';
      reason:
        | 'missing'
        | 'expired'
        | 'server-failed'
        | 'userMismatch'
        | 'invalid-webview-jwt';
      source?: EntitlementSource;
      error?: unknown;
    };

export const Local = {
  valid: (source: LocalSource, e: EntitlementWithToken): LocalFoundValid => ({
    kind: 'local',
    source,
    state: 'valid',
    entitlement: e,
  }),
  expired: (
    source: LocalSource,
    e: EntitlementWithToken,
  ): LocalFoundExpired => ({
    kind: 'local',
    source,
    state: 'expired',
    entitlement: e,
  }),
  missing: (source: LocalSource): LocalMissing => ({
    kind: 'local',
    source,
    state: 'missing',
  }),
  userMismatch: (
    source: LocalSource,
    e: EntitlementDetails,
  ): LocalFoundUserMismatch => ({
    kind: 'local',
    source,
    state: 'userMismatch',
    entitlement: e,
  }),
};

export const isLocalValid = (p: LocalCheck): p is LocalFoundValid =>
  p.kind === 'local' && p.state === 'valid';
export const isLocalExpired = (p: LocalCheck): p is LocalFoundExpired =>
  p.kind === 'local' && p.state === 'expired';

export const isLocalMissing = (p: LocalCheck): p is LocalMissing =>
  p.kind === 'local' && p.state === 'missing';

export const isLocalUserMismatch = (
  p: LocalCheck,
): p is LocalFoundUserMismatch =>
  p.kind === 'local' && p.state === 'userMismatch';

export const request = {
  create: (payload: CreateSessionRequestPayload): EntitlementRequest => ({
    action: 'create',
    endpoint: 'session/create',
    payload,
  }),
  refresh: (payload: RefreshSessionRequestPayload): EntitlementRequest => ({
    action: 'refresh',
    endpoint: 'session/refresh',
    payload,
  }),
};
